/**
 * @file SessionContext.ts
 *
 * Manages a single real-time speech recognition and translation session using the Azure
 * Cognitive Services Speech SDK. This is the standard (non-hybrid) session implementation,
 * used when `modelType` is `onlyStableModel` or any other non-hybrid variant.
 *
 * Responsibilities:
 * - Instantiates and configures either a `SpeechRecognizer` (transcription-only, when no
 *   output languages are selected) or a `TranslationRecognizer` (transcription + translation,
 *   when one or more output languages are configured).
 * - Creates and owns one `Text2SpeechProcessor` per output language, each responsible for
 *   queuing and synthesizing translated speech audio via the Azure TTS API.
 * - Propagates recognition events (`recognizing`, `recognized`, `canceled`) to caller-supplied
 *   callbacks for UI state updates and Redux dispatch.
 * - Exposes `setSpeechToken()` to propagate token refreshes to all owned SDK instances and
 *   TTS processors simultaneously.
 * - Exposes `changeInputDevice()` to hot-swap the microphone during an active session by
 *   tearing down and rebuilding the recognizer against the new audio stream.
 * - Exposes `setEchoCancellation()` to switch the capture's AEC live (used by Two-Way to
 *   disable AEC while the Floor Feed is rendering the mic's own signal, and restore it after).
 * - Follows a `READY` → `RUNNING` → `STOPPED` state machine; transitions are enforced to
 *   prevent invalid operations (e.g., device changes when not RUNNING).
 *
 * Pipeline position:
 *   AudioInputHandler → [SpeechRecognizer | TranslationRecognizer] (this file)
 *     → TranslationProcessor / Text2SpeechProcessor → AudioPlayer
 *
 * Also exports:
 * - `loadAvailableLanguages()` — fetches the full Azure Translation language list at runtime.
 * - `buildLanguageMatrixV2()` — cross-references recognition, translation, and TTS JSON
 *   data sources to produce the `RecognizableAndTranslatableLanguage[]` and
 *   `TranslatableAndSpeakableLanguage[]` arrays used to populate the UI language selectors.
 *   Preview/legacy voices (suffixed with '1', '2', or '3') are excluded from the matrix.
 *
 * Instantiated and managed exclusively through `SessionResolver.createSessionContext()`.
 */
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import {PropertyId, SpeechRecognizer, TranslationRecognizer} from 'microsoft-cognitiveservices-speech-sdk';
import {TranslationProcessor} from './TranslationProcessor';
import {
    BiDirectionalLanguage,
    dedupeOutputsByLanguage,
    GlossaryTerm,
    Input, isDevEnvironment,
    Language,
    Output,
    RawSpeakRecognitionL,
    RawText2SpeechL,
    RecognizableAndTranslatableLanguage,
    TranslatableAndSpeakableLanguage,
    TranslationResult,
    TranslationsBody,
    TtsVoice
} from './Configuration';
import {Text2SpeechProcessor} from '../core/Text2SpeechProcessor';

import untypedSpeakRecognitionLanguages from '../SpeakRecognitionSupportedLanguages_V1.json';
import untypedText2SpeechLanguages from '../Text2SpeechSupportedLanguages_V1.json';
import untypedTranslationLanguages from '../translationLanguages.json';
import {CancellationReason} from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports';
import {UUID} from 'crypto';
import MediaDevicesUtils from "../utils/MediaDevicesUtils";
import MediaDeviceInfoWithState from "../utils/MediaDeviceInfoPlus";
import BenchmarkCollector from "../benchmark/BenchmarkCollector";
import {getTargetLanguageValidationError} from './SessionValidation';

const azureRegion = 'eastus';

const LOG_PREFIX = '[SessionContext]';

export class SessionContext {

    public static currentRecognizedTokens: string[] | null = null;
    private sessionId: UUID;
    private modelType: string;
    private selectedOutputs: Output[];
    private selectedInput: Input;
    private inputLangKey: string;
    private azureSpeechAPIToken: string;
    private onPartialRecognitionReady: (recognizedText: string) => void;
    private onRecognitionReady: (recognizedText: string) => void;
    private onPartialTranslationReady: (translationReady: Map<Language, TranslationResult>) => void;
    private onTranslationReady: (translationBody: TranslationsBody) => void;
    private onSessionCanceled: (sessionId: UUID, azureSessionId: string, reason: CancellationReason, errorMsg: string) => void;

    private useDictionary: boolean;
    private glossaryTerms: GlossaryTerm[];
    // Applied to every recognition mic (re)acquisition. Dashboard sessions keep this true.
    // Two-Way sessions toggle it live via setEchoCancellation(): the Floor Feed plays the
    // mic's own signal back out, which poisons Chrome's AEC reference (muffled floor audio,
    // TTS leaking into recognition), so AEC is off while the feed runs and on otherwise.
    private echoCancellation: boolean;
    // Serializes setEchoCancellation() calls so rapid Floor Feed toggles cannot
    // interleave capture re-acquisitions.
    private aecToggleChain: Promise<void> = Promise.resolve();

    private speechRecognitionConfig: sdk.SpeechTranslationConfig | undefined;
    private speechRecognizer: SpeechRecognizer | undefined;
    private translationRecognizer: TranslationRecognizer | undefined;
    private translationProcessor: TranslationProcessor | undefined;
    private speech2TextProcessorByLanguage: Map<string, Text2SpeechProcessor>;
    private state: 'READY' | 'RUNNING' | 'STOPPED';

    public setSpeechToken(newToken: string) {
        this.azureSpeechAPIToken = newToken;

        this.speech2TextProcessorByLanguage?.forEach(processor => {
            processor.setSpeechToken(newToken);
        });

        if (this.speechRecognizer) {
            this.speechRecognizer.authorizationToken = newToken;
        }

        if (this.translationRecognizer) {
            this.translationRecognizer.authorizationToken = newToken;
        }

        if (isDevEnvironment()) {
            console.log(`${LOG_PREFIX} Azure token refreshed for all processors`);
        }
    }

    constructor(modelType: string, azureSpeechAPIToken: string, sessionId: UUID, selectedInput: Input, selectedOutputs: Output[],
                onPartialRecognitionReady: (recognizedText: string) => void,
                onRecognitionReady: (recognizedText: string) => void,
                onPartialTranslationReady: (translationReady: Map<Language, TranslationResult>) => void,
                onTranslationReady: (translationBody: TranslationsBody) => void,
                onSessionCanceled: (sessionId: UUID, azureSessionId: string, reason: CancellationReason, errorMsg: string) => void,
                useDictionary: boolean = false,
                glossaryTerms: GlossaryTerm[] = [],
                echoCancellation: boolean = false) {

        this.modelType = modelType;
        this.sessionId = sessionId;
        this.useDictionary = useDictionary;
        this.echoCancellation = echoCancellation;
        this.glossaryTerms = glossaryTerms;
        this.selectedInput = selectedInput;
        this.inputLangKey = selectedInput.translationKey || selectedInput.language.recognitionKey.split('-')[0];
        this.selectedOutputs = dedupeOutputsByLanguage(selectedOutputs);
        this.azureSpeechAPIToken = azureSpeechAPIToken;
        this.onPartialRecognitionReady = onPartialRecognitionReady;
        this.onRecognitionReady = onRecognitionReady;
        this.onPartialTranslationReady = onPartialTranslationReady;
        this.onTranslationReady = onTranslationReady;
        this.onSessionCanceled = onSessionCanceled;

        if (isDevEnvironment()) {
            console.log(`${LOG_PREFIX} Initializing — input: ${selectedInput.language.recognitionKey}, outputs: ${selectedOutputs.map(o => o.language.translationKey).join(', ')}`);
        }

        this.speech2TextProcessorByLanguage = new Map<string, Text2SpeechProcessor>();
        this.selectedOutputs.forEach(output => {
            this.speech2TextProcessorByLanguage.set(output.language.translationKey, this.startText2SpeechProcessor(output, azureSpeechAPIToken));
            if (isDevEnvironment()) {
                console.log(`${LOG_PREFIX} Text2Speech processor started — speakKey: ${output.language.speakKey}`);
            }
        });

        this.state = 'READY';
        if (isDevEnvironment()) {
            console.log(`${LOG_PREFIX} Ready`);
        }
    }

    public static processPartialRecognition(partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        if (!SessionContext.currentRecognizedTokens)
            SessionContext.currentRecognizedTokens = [];

        const tokens = partiallyRecognizedText.match(/[^.;?!,]+[.;?!,]?/g);
        if (!tokens) return;

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];

            if (!SessionContext.currentRecognizedTokens[i]) {
                if (!['.', ';', '?', '!', ','].includes(token.charAt(token.length - 1))) return;
                SessionContext.currentRecognizedTokens.push(token);
                onRecognitionReady(token);
            }
        }
    }

    public start() {
        if (!this.useDictionary) {
            const validationError = getTargetLanguageValidationError(this.inputLangKey, this.selectedOutputs);
            if (validationError) return Promise.reject(validationError);
        }

        return MediaDevicesUtils.instance().getInputDeviceStream(this.selectedInput.device.deviceId, this.selectedInput.gain, this.echoCancellation)
            .then(inputMediaStream => {
                if (!inputMediaStream) {
                    throw new Error('Cannot start session. Cannot get audio from microphone. Check the microphone is properly connected and able to provide audio input. If the microphone is connected, please check you have granted permissions to it');
                }

                this.setupRecognition(inputMediaStream);

                try {
                    this.speech2TextProcessorByLanguage?.forEach(processor => {
                        processor.start()
                    });
                    this.translationProcessor?.start();
                    this.speechRecognizer?.startContinuousRecognitionAsync();
                    this.translationRecognizer?.startContinuousRecognitionAsync();
                } catch (e) {
                    console.error(`${LOG_PREFIX} Error starting processors:`, e);
                    this.speech2TextProcessorByLanguage?.forEach(processor => {
                        try {
                            processor.stop()
                        } catch (ignore) {
                        }
                    });
                    try {
                        this.speechRecognizer?.stopContinuousRecognitionAsync();
                        this.translationRecognizer?.stopContinuousRecognitionAsync();
                    } catch (ignore) {
                    }
                }

                this.state = 'RUNNING';
                if(isDevEnvironment()) {
                    console.log(`${LOG_PREFIX} Started`);
                }
            })
            .catch(err => {
                const message = 'Cannot start session. The selected microphone is not connected or is not active. ' +
                    'Please check the microphone is properly connected and able to provide audio input.';
                console.error(`${LOG_PREFIX} Failed to start:`, err);
                throw new Error(message);
            })
    }

    // Parses raw Azure translation results into a typed map of TranslationResult objects.
    private parseTranslations(
        recognizedText: string,
        azureTranslations: sdk.Translations
    ): Map<Language, TranslationResult> {
        const parsedTranslations = new Map<Language, TranslationResult>();
        azureTranslations.languages.forEach((translationKey: string) => {
            const translatedText = azureTranslations.get(translationKey);
            const lang = {id: translationKey} as Language;
            parsedTranslations.set(lang, {
                originalText: recognizedText || '',
                translatedText,
                status: '',
                language: lang
            } as TranslationResult);
        });
        return parsedTranslations;
    }

    private setupRecognition(inputMediaStream: MediaStream) {
        const audioConfig = sdk.AudioConfig.fromStreamInput(inputMediaStream);
        this.speechRecognitionConfig = sdk.SpeechTranslationConfig.fromAuthorizationToken(this.azureSpeechAPIToken, azureRegion);
        if (!this.speechRecognitionConfig) {
            throw new Error('No speech recognition configuration found.');
        }
        this.speechRecognitionConfig.setProperty(PropertyId.Speech_SegmentationSilenceTimeoutMs, '400');
        this.speechRecognitionConfig.speechRecognitionLanguage = this.selectedInput.language.recognitionKey;
        this.selectedOutputs.forEach(output => {
            if (output.language.translationKey !== this.inputLangKey) {
                // @ts-ignore
                this.speechRecognitionConfig.addTargetLanguage(output.language.translationKey);
            }
        });

        if (this.selectedOutputs.length === 0) {
            // Transcription-only mode: use a plain speech recognizer
            this.speechRecognizer = new sdk.SpeechRecognizer(this.speechRecognitionConfig, audioConfig);

            if (this.glossaryTerms.length > 0) {
                const phraseList = sdk.PhraseListGrammar.fromRecognizer(this.speechRecognizer);
                this.glossaryTerms.forEach(term => phraseList.addPhrase(term.source));
            }

            this.speechRecognizer.recognizing = (sender, event) => {
                this.onPartialRecognitionReady(event.result.text);
            }

            this.speechRecognizer.recognized = (sender, event) => {
                const recognizedText = event.result.text;
                BenchmarkCollector.instance().captureRawRecognition(recognizedText);
                this.onRecognitionReady(recognizedText);
            }

            this.speechRecognizer.canceled = (sender, event) => {
                this.onSessionCanceled(this.sessionId, event.sessionId, event.reason, event.errorDetails);
            }

        } else if (this.useDictionary) {
            // Dictionary mode: SpeechRecognizer (STT only) + TranslationProcessor (REST + glossary)
            const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(this.azureSpeechAPIToken, azureRegion);
            speechConfig.setProperty(PropertyId.Speech_SegmentationSilenceTimeoutMs, '400');
            speechConfig.speechRecognitionLanguage = this.selectedInput.language.recognitionKey;

            this.speechRecognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

            if (this.glossaryTerms.length > 0) {
                const phraseList = sdk.PhraseListGrammar.fromRecognizer(this.speechRecognizer);
                this.glossaryTerms.forEach(term => phraseList.addPhrase(term.source));
            }

            this.translationProcessor = this.startTranslationProcessor(
                this.selectedOutputs.map(o => o.language),
                this.selectedInput.language,
                this.glossaryTerms
            );

            this.speechRecognizer.recognizing = (sender, event) => {
                this.onPartialRecognitionReady(event.result.text);
            }

            this.speechRecognizer.recognized = (sender, event) => {
                const recognizedText = event.result.text;
                BenchmarkCollector.instance().captureRawRecognition(recognizedText);
                this.onRecognitionReady(recognizedText);
                SessionContext.currentRecognizedTokens = [];
                this.translationProcessor?.enqueueText(recognizedText);
            }

            this.speechRecognizer.canceled = (sender, event) => {
                this.onSessionCanceled(this.sessionId, event.sessionId, event.reason, event.errorDetails);
            }

        } else {
            // Transcription + translation mode: use a translation recognizer
            this.translationRecognizer = new sdk.TranslationRecognizer(this.speechRecognitionConfig, audioConfig);

            if (this.glossaryTerms.length > 0) {
                const phraseList = sdk.PhraseListGrammar.fromRecognizer(this.translationRecognizer);
                this.glossaryTerms.forEach(term => phraseList.addPhrase(term.source));
            }

            this.translationRecognizer.recognizing = (sender, event) => {
                const partialText = event.result.text;
                this.onPartialRecognitionReady(partialText);

                const parsedTranslations = this.parseTranslations(partialText, event.result.translations);
                parsedTranslations.forEach((translationResult) => {
                    const langId = translationResult.language.id;
                    const isUrdu = langId === 'ur';
                    const isStableModelOnly = this.modelType === 'onlyStableModel';
                    if (!isStableModelOnly && !isUrdu) {
                        this.speech2TextProcessorByLanguage?.get(langId)?.processPartialRecognition(
                            translationResult.translatedText,
                            text => this.speech2TextProcessorByLanguage?.get(langId)?.enqueueText(text)
                        );
                    }
                });

                this.onPartialTranslationReady(parsedTranslations);
            }

            this.translationRecognizer.recognized = (sender, event) => {
                const recognizedText = event.result.text;
                BenchmarkCollector.instance().captureRawRecognition(recognizedText);
                this.onRecognitionReady(recognizedText);

                const parsedTranslations = this.parseTranslations(recognizedText, event.result.translations);
                const translationsBody: TranslationsBody = {inputs: {}, outputs: {}};

                parsedTranslations.forEach((translationResult, lang) => {
                    const langId = lang.id;
                    BenchmarkCollector.instance().captureRawTranslation(langId, translationResult.translatedText);
                    translationsBody.outputs[langId] = translationResult.translatedText;
                    if (!translationsBody.inputs[this.inputLangKey]) {
                        translationsBody.inputs[this.inputLangKey] = recognizedText;
                    }

                    this.speech2TextProcessorByLanguage?.get(langId)?.processRecognition(
                        this.modelType,
                        translationResult.translatedText,
                        text => this.speech2TextProcessorByLanguage?.get(langId)?.enqueueText(text)
                    );
                });

                SessionContext.currentRecognizedTokens = [];
                this.onTranslationReady(translationsBody);
            }

            this.translationRecognizer.sessionStarted = (sender, event) => {
                if(isDevEnvironment()) {
                    console.log(`${LOG_PREFIX} TranslationRecognizer session started — azureSessionId: ${event.sessionId}`);
                }
            }
            this.translationRecognizer.sessionStopped = (sender, event) => {
                if(isDevEnvironment()) {
                    console.log(`${LOG_PREFIX} TranslationRecognizer session stopped — azureSessionId: ${event.sessionId}`);
                }
            }
            this.translationRecognizer.canceled = (sender, event) => {
                console.error(`${LOG_PREFIX} TranslationRecognizer canceled — reason: ${event.reason}, error: ${event.errorDetails}`);
                this.onSessionCanceled(this.sessionId, event.sessionId, event.reason, event.errorDetails);
            }
        }
    }

    public changeInputDevice(selectedInput: MediaDeviceInfo) {
        if (this.state !== 'RUNNING')
            return;

        if (!(selectedInput as MediaDeviceInfoWithState).isActive) {
            throw new Error('Selected microphone is disconnected or inactive. Please check the microphone is properly connected and able to provide audio input.');
        }

        if (this.selectedInput?.device?.deviceId) {
            MediaDevicesUtils.instance().freeAudioHandler(this.selectedInput.device.deviceId)
        }

        this.selectedInput = {
            ...this.selectedInput,
            device: selectedInput
        };

        MediaDevicesUtils.instance().getInputDeviceStream(this.selectedInput.device.deviceId, this.selectedInput.gain, this.echoCancellation)
            .then(inputMediaStream => {
                if (!inputMediaStream) {
                    throw new Error('Selected microphone is not producing audio. Please check the microphone is properly connected and able to provide audio input.');
                }

                this.speechRecognizer?.stopContinuousRecognitionAsync();
                this.translationRecognizer?.stopContinuousRecognitionAsync();

                try {
                    this.speechRecognizer?.close();
                } catch {
                }
                try {
                    this.translationRecognizer?.close();
                } catch {
                }

                this.setupRecognition(inputMediaStream);

                try {
                    this.translationProcessor?.start();
                    this.speechRecognizer?.startContinuousRecognitionAsync();
                    this.translationRecognizer?.startContinuousRecognitionAsync();
                } catch (e) {
                    console.error(`${LOG_PREFIX} Error switching input device:`, e);
                    try {
                        this.translationProcessor?.stop();
                        this.speechRecognizer?.stopContinuousRecognitionAsync();
                        this.translationRecognizer?.stopContinuousRecognitionAsync();
                    } catch (ignore) {
                    }
                }

                if(isDevEnvironment()) {
                    console.log(`${LOG_PREFIX} Input device changed — deviceId: ${this.selectedInput.device.deviceId}`);
                }
            })
    }

    /**
     * Switches the recognition capture's echo cancellation live. AEC cannot be reliably
     * reconfigured on a live track, so this re-acquires the microphone with the new
     * setting (getInputDeviceStream frees the mismatched shared capture) and rebinds the
     * recognizer — the same mechanics as changeInputDevice(), on the same device.
     * Recognition pauses only for the brief re-acquisition; the session keeps running.
     *
     * Two-Way calls this with `false` before starting the Floor Feed and with `true`
     * after stopping it.
     */
    public setEchoCancellation(enabled: boolean): Promise<void> {
        this.aecToggleChain = this.aecToggleChain
            .catch(() => {
            })
            .then(() => this.applyEchoCancellation(enabled));
        return this.aecToggleChain;
    }

    private applyEchoCancellation(enabled: boolean): Promise<void> {
        if (this.echoCancellation === enabled) return Promise.resolve();

        this.echoCancellation = enabled;
        // Before start() acquires the mic (READY) the new value is picked up there;
        // after stop() there is nothing to rewire.
        if (this.state !== 'RUNNING') return Promise.resolve();

        const deviceId = this.selectedInput?.device?.deviceId;
        if (!deviceId) return Promise.resolve();

        return MediaDevicesUtils.instance().getInputDeviceStream(deviceId, this.selectedInput.gain, enabled)
            .then(inputMediaStream => {
                if (!inputMediaStream) {
                    throw new Error('Microphone unavailable while switching echo cancellation.');
                }

                this.speechRecognizer?.stopContinuousRecognitionAsync();
                this.translationRecognizer?.stopContinuousRecognitionAsync();
                try {
                    this.speechRecognizer?.close();
                } catch {
                }
                try {
                    this.translationRecognizer?.close();
                } catch {
                }

                this.setupRecognition(inputMediaStream);

                try {
                    this.speechRecognizer?.startContinuousRecognitionAsync();
                    this.translationRecognizer?.startContinuousRecognitionAsync();
                } catch (e) {
                    console.error(`${LOG_PREFIX} Error rebinding recognizer after echo cancellation switch:`, e);
                }

                if (enabled) {
                    // The fresh AEC-on capture's canceller gets no reference tap from playout
                    // streams that predate it, so long-lived TTS AudioContexts play audio it
                    // cannot cancel (the mic re-hears TTS while getSettings() reports AEC on).
                    // Rebuilding each TTS player's context AFTER the new capture registers its
                    // playout with the fresh canceller — what a full session restart does, and
                    // empirically what clears the feedback loop.
                    this.speech2TextProcessorByLanguage?.forEach(processor => {
                        try {
                            processor.resetAudioOutput();
                        } catch (e) {
                            console.error(`${LOG_PREFIX} Error resetting TTS output after echo cancellation switch:`, e);
                        }
                    });
                }

                if(isDevEnvironment()) {
                    console.log(`${LOG_PREFIX} Echo cancellation ${enabled ? 'enabled' : 'disabled'} — recognizer rebound${enabled ? ', TTS playback contexts rebuilt' : ''}`);
                }
            });
    }

    public changeInputLanguage(newInput: Input, allOutputs: Output[]): Promise<void> {
        if (this.state !== 'RUNNING') return Promise.resolve();

        const nextInputLangKey = newInput.translationKey || newInput.language.recognitionKey.split('-')[0];
        const nextOutputs = dedupeOutputsByLanguage(allOutputs);
        if (!this.useDictionary) {
            const validationError = getTargetLanguageValidationError(nextInputLangKey, nextOutputs);
            if (validationError) return Promise.reject(validationError);
        }

        const previousDeviceId = this.selectedInput?.device?.deviceId;
        const nextDeviceId = newInput.device.deviceId;
        if (previousDeviceId && previousDeviceId !== nextDeviceId) {
            MediaDevicesUtils.instance().freeAudioHandler(this.selectedInput.device.deviceId);
        }

        this.selectedInput = newInput;
        this.inputLangKey = nextInputLangKey;
        this.selectedOutputs = nextOutputs;

        this.speechRecognizer?.stopContinuousRecognitionAsync();
        this.translationRecognizer?.stopContinuousRecognitionAsync();
        try {
            this.speechRecognizer?.close();
        } catch {
        }
        try {
            this.translationRecognizer?.close();
        } catch {
        }
        this.speechRecognizer = undefined;
        this.translationRecognizer = undefined;

        // Ensure TTS processors exist for all output languages
        this.selectedOutputs.forEach(output => {
            const key = output.language.translationKey;
            if (!this.speech2TextProcessorByLanguage.has(key)) {
                this.speech2TextProcessorByLanguage.set(key, this.startText2SpeechProcessor(output, this.azureSpeechAPIToken));
                this.speech2TextProcessorByLanguage.get(key)?.start();
                if(isDevEnvironment()) {
                console.log(`${LOG_PREFIX} Created TTS processor for new output — key: ${key}`);
                }
            }
        });

        return MediaDevicesUtils.instance().getInputDeviceStream(newInput.device.deviceId, newInput.gain, this.echoCancellation)
            .then(inputMediaStream => {
                if (!inputMediaStream) throw new Error('Cannot switch input language. Microphone unavailable.');
                this.setupRecognition(inputMediaStream);
                try {
                    this.speechRecognizer?.startContinuousRecognitionAsync();
                    this.translationRecognizer?.startContinuousRecognitionAsync();
                } catch (e) {
                    console.error(`${LOG_PREFIX} Error restarting recognizer after language switch:`, e);
                }
                if(isDevEnvironment()) {
                    console.log(`${LOG_PREFIX} Input language changed — newLang: ${newInput.language.recognitionKey}`);
                }
            });
    }

    public stop() {
        if (this.state === 'STOPPED') return;

        try {
            this.speechRecognizer?.stopContinuousRecognitionAsync();
        } catch {
        }
        try {
            this.speechRecognizer?.close();
        } catch {
        }
        try {
            this.translationRecognizer?.stopContinuousRecognitionAsync();
        } catch {
        }
        try {
            this.translationRecognizer?.close();
        } catch {
        }
        try {
            this.translationProcessor?.stop();
        } catch {
        }
        this.speechRecognizer = undefined;
        this.translationRecognizer = undefined;
        this.speech2TextProcessorByLanguage?.forEach((processor, langId) => {
            if(isDevEnvironment()) {
                console.log(`${LOG_PREFIX} Stopping Text2Speech processor — langId: ${langId}`);
            }
            try {
                processor.stop();
            } catch {
            }
        });
        this.state = 'STOPPED';
        console.log(`${LOG_PREFIX} Stopped`);
    }

    public getState() {
        return this.state;
    }

    private startTranslationProcessor = (
        outputLanguages: TranslatableAndSpeakableLanguage[],
        inputLanguage: RecognizableAndTranslatableLanguage,
        glossaryTerms: GlossaryTerm[] = []
    ): TranslationProcessor => {

        return new TranslationProcessor(this.sessionId, inputLanguage, outputLanguages, glossaryTerms,
            (_translationsReady) => {
                const translationsBody: TranslationsBody = {inputs: {}, outputs: {}};

                _translationsReady.forEach((translationResult, lang) => {
                    const langId = lang.id;
                    BenchmarkCollector.instance().captureRawTranslation(langId, translationResult.translatedText);
                    translationsBody.outputs[langId] = translationResult.translatedText;
                    translationsBody.inputs[this.inputLangKey] = translationResult.originalText;

                    this.speech2TextProcessorByLanguage?.get(langId)?.processRecognition(
                        this.modelType,
                        translationResult.translatedText,
                        text => this.speech2TextProcessorByLanguage?.get(langId)?.enqueueText(text)
                    );
                });

                this.onPartialTranslationReady(_translationsReady);
                this.onTranslationReady(translationsBody);
            });
    }

    private startText2SpeechProcessor = (outputConfig: Output, azureSpeechAPIToken: string): Text2SpeechProcessor => {
        return new Text2SpeechProcessor(outputConfig, azureSpeechAPIToken);
    }

    public getText2Speech(translationKey: string) {
        return this.speech2TextProcessorByLanguage?.get(translationKey);
    }

    public setOutputVoice(translationKey: string, voiceName: string): void {
        this.speech2TextProcessorByLanguage?.get(translationKey)?.setVoiceName(voiceName);
    }

    public setOutputDevice(translationKey: string, device: MediaDeviceInfo | null, gain?: number, force: boolean = false): void {
        this.speech2TextProcessorByLanguage?.get(translationKey)?.setOutputDevice(device, gain, force);
    }
}

export const loadAvailableLanguages = async () => {
    const uri = 'https://api.cognitive.microsofttranslator.com/Languages?api-version=3.0&scope=translation';
    const r = await fetch(uri, {method: 'get'})
        .then(response => response.json())
        .catch(error => {
            console.error('[loadAvailableLanguages] Failed to fetch languages:', error);
            return {translation: new Map<String, Language>()}
        });

    return Object.keys(r.translation).map(langKey => ({
        id: langKey,
        name: r.translation[langKey].name,
        nativeName: r.translation[langKey].nativeName
    })) as Language[];
}

// Maps translation keys that don't follow the standard `<key>-<region>` pattern
// to their corresponding TTS speak keys.
const TRANSLATION_TO_SPEAK_KEY_OVERRIDES: Record<string, string> = {
    'zh-Hans': 'zh-CN',
    'zh-Hant': 'zh-HK',
    'sr-Latn': 'sr-LATN-RS',
    'sr-Cyrl': 'sr-RS',
};

const NEURAL_VOICE_SUFFIXES_TO_EXCLUDE = ['1', '2', '3'];

const isPreviewVoice = (voiceName: string) =>
    NEURAL_VOICE_SUFFIXES_TO_EXCLUDE.some(suffix => voiceName.endsWith(suffix));

export const buildLanguageMatrixV2 = async () => {
    const supportedRecognitionJson: RawSpeakRecognitionL = untypedSpeakRecognitionLanguages;
    const supportedSpeakableJson: RawText2SpeechL = untypedText2SpeechLanguages as unknown as RawText2SpeechL;

    const supportedRecognition = Object.keys(supportedRecognitionJson);
    const supportedTranslations = untypedTranslationLanguages.translation;
    const supportedSpeakable = Object.keys(supportedSpeakableJson);

    const recognizableAndTranslatable: RecognizableAndTranslatableLanguage[] = supportedRecognition.map(recognitionKey => ({
        name: supportedRecognitionJson[recognitionKey],
        recognitionKey
    }));

    const translatableAndSpeakable: TranslatableAndSpeakableLanguage[] = [];

    Object.keys(supportedTranslations).forEach(translationKey => {
        const overrideSpeakKey = TRANSLATION_TO_SPEAK_KEY_OVERRIDES[translationKey];

        if (overrideSpeakKey) {
            translatableAndSpeakable.push({
                name: supportedSpeakableJson[overrideSpeakKey].name,
                voices: supportedSpeakableJson[overrideSpeakKey].voices.filter((v: TtsVoice) => !isPreviewVoice(v.name)),
                speakKey: overrideSpeakKey,
                translationKey
            });
        } else {
            supportedSpeakable
                .filter(key => key.startsWith(translationKey + '-'))
                .forEach(speakKey => {
                    const voices = supportedSpeakableJson[speakKey].voices.filter((v: TtsVoice) => !isPreviewVoice(v.name));
                    if (voices.length > 0) {
                        translatableAndSpeakable.push({
                            name: supportedSpeakableJson[speakKey].name,
                            voices,
                            speakKey,
                            translationKey
                        });
                    }
                });
        }
    });

    return {a: recognizableAndTranslatable, b: translatableAndSpeakable};
}

export const buildBiDirectionalLanguageMatrix = async (): Promise<BiDirectionalLanguage[]> => {
    const {a: recognizableLanguages, b: speakableLanguages} = await buildLanguageMatrixV2();

    const result: BiDirectionalLanguage[] = [];

    speakableLanguages.forEach(speakable => {
        // Derive the locale from the speakKey (e.g. "en-GB-LibbyNeural" → "en-GB")
        const speakLocale = speakable.speakKey.split('-').slice(0, 2).join('-');

        // Prefer an exact locale match; fall back to any recognition key that starts
        // with the same language root so languages without an exact match still appear.
        const matchingRecognition =
            recognizableLanguages.find(r => r.recognitionKey === speakLocale) ||
            recognizableLanguages.find(r =>
                r.recognitionKey.startsWith(speakable.translationKey + '-')
            );

        if (matchingRecognition) {
            result.push({
                name: speakable.name,
                recognitionKey: matchingRecognition.recognitionKey,
                translationKey: speakable.translationKey,
                speakKey: speakable.speakKey,
                voices: speakable.voices,
            });
        }
    });

    return result.sort((a, b) => a.name.localeCompare(b.name));
}
